import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { ProjectDetail } from './ProjectHome';

const MODULE_LABELS: Record<string, string> = {
  documents: 'Documents (cycle v1 → v2 → validé)',
  taches: 'Tâches bidirectionnelles',
  communication: 'Discussion / réunions / décisions',
  espace_perso: 'Espace perso (notes privées)',
  ia: 'Assistant IA (boutons préremplis)',
  ifc: 'Viewer IFC (@thatopen/components)',
  calendrier: 'Calendrier (vue mensuelle)',
  checklist: 'Checklist de projet (jalons)',
};

type Delegation = {
  id: number;
  from_user: number;
  from_name: string;
  to_user: number;
  to_name: string;
  active: number;
  note: string | null;
  created_at: string;
  expires_at: string | null;
};

export default function ProjectSettings() {
  const detail = useOutletContext<ProjectDetail>();
  const { id: projectId } = useParams<{ id: string }>();
  const nav = useNavigate();

  const [modules, setModules] = useState(
    new Map(detail.settings.map((s) => [s.module_key, !!s.enabled]))
  );
  const [contextText, setContextText] = useState(detail.context_text ?? '');
  const [saving, setSaving] = useState(false);

  const [projectName, setProjectName] = useState(detail.project.name);
  const [projectColor, setProjectColor] = useState(detail.project.theme_color);

  // Nomenclature
  const [nomenclaturePattern, setNomenclaturePattern] = useState('');
  const [nomenclatureEnabled, setNomenclatureEnabled] = useState(false);

  // Délégations
  const [delegations, setDelegations] = useState<Delegation[]>([]);
  const [delegateTo, setDelegateTo] = useState<string>('');
  const [delegateNote, setDelegateNote] = useState('');
  const [delegateExpires, setDelegateExpires] = useState('');

  // Gabarits
  const [templates, setTemplates] = useState<Array<{ id: number; name: string; description: string | null }>>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  useEffect(() => {
    if (!projectId) return;
    api.get<{ nomenclature: { pattern: string; enabled: number } | null }>(
      `/projects/${projectId}/nomenclature`
    ).then((r) => {
      if (r.nomenclature) {
        setNomenclaturePattern(r.nomenclature.pattern ?? '');
        setNomenclatureEnabled(!!r.nomenclature.enabled);
      }
    });
    api.get<{ delegations: Delegation[] }>(
      `/projects/${projectId}/delegations`
    ).then((r) => setDelegations(r.delegations));
    api.get<{ templates: typeof templates }>('/templates').then((r) => setTemplates(r.templates));
  }, [projectId]);

  async function toggle(key: string) {
    const newVal = !modules.get(key);
    const next = new Map(modules);
    next.set(key, newVal);
    setModules(next);
    await api.patch(`/projects/${projectId}/settings`, { module_key: key, enabled: newVal });
  }

  async function saveContext() {
    setSaving(true);
    try {
      await api.patch(`/projects/${projectId}/context`, { context_text: contextText });
    } finally {
      setSaving(false);
    }
  }

  async function saveProjectInfo() {
    await api.patch(`/projects/${projectId}`, {
      name: projectName,
      theme_color: projectColor,
    });
    location.reload();
  }

  async function saveNomenclature() {
    await api.patch(`/projects/${projectId}/nomenclature`, {
      pattern: nomenclaturePattern,
      enabled: nomenclatureEnabled,
    });
  }

  async function createDelegation() {
    if (!delegateTo) return;
    await api.post(`/projects/${projectId}/delegations`, {
      to_user: Number(delegateTo),
      note: delegateNote || null,
      expires_at: delegateExpires || null,
    });
    setDelegateTo('');
    setDelegateNote('');
    setDelegateExpires('');
    const r = await api.get<{ delegations: Delegation[] }>(`/projects/${projectId}/delegations`);
    setDelegations(r.delegations);
  }

  async function revokeDelegation(id: number) {
    await api.del(`/delegations/${id}`);
    const r = await api.get<{ delegations: Delegation[] }>(`/projects/${projectId}/delegations`);
    setDelegations(r.delegations);
  }

  async function applyTemplate() {
    if (!selectedTemplate) return;
    if (!confirm("Appliquer ce gabarit va remplacer les modules, catégories, workflow et dossiers par défaut. Continuer ?")) return;
    await api.post(`/projects/${projectId}/apply-template/${selectedTemplate}`, {});
    location.reload();
  }

  async function archiveProject() {
    if (!confirm('Archiver ce projet ? Il disparaît de la liste active (récupérable via Voir archivés).')) return;
    await api.patch(`/projects/${projectId}/archive`, { archived: true });
    nav('/projets');
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Identité du projet</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, marginTop: 10 }}>
          <div className="field">
            <label>Nom</label>
            <input value={projectName} onChange={(e) => setProjectName(e.target.value)} />
          </div>
          <div className="field">
            <label>Couleur</label>
            <input
              type="color"
              value={projectColor}
              onChange={(e) => setProjectColor(e.target.value)}
              style={{ width: 60, padding: 2, height: 40 }}
            />
          </div>
        </div>
        <button className="btn btn-primary" onClick={saveProjectInfo}>
          Enregistrer
        </button>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Modules du projet</div>
        <div className="card-meta" style={{ marginBottom: 12 }}>
          Les onglets n'apparaissent qu'une fois activés.
        </div>
        {Object.keys(MODULE_LABELS).map((key) => {
          const on = modules.get(key) ?? false;
          return (
            <div
              key={key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderTop: '1px solid var(--c-border)',
              }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>{MODULE_LABELS[key]}</div>
                <div className="card-meta">clé : {key}</div>
              </div>
              <button
                onClick={() => toggle(key)}
                className={`btn ${on ? 'btn-valider' : 'btn-ghost'}`}
                style={{ minWidth: 90 }}
              >
                {on ? 'Activé' : 'Désactivé'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Mémoire IA du projet</div>
        <div className="card-meta" style={{ marginBottom: 8 }}>
          Contexte injecté à chaque appel IA : charte, contraintes, décisions passées.
        </div>
        <textarea
          rows={6}
          value={contextText}
          onChange={(e) => setContextText(e.target.value)}
          style={{
            width: '100%',
            padding: 10,
            border: '1px solid var(--c-border)',
            borderRadius: 6,
            background: 'var(--c-bg)',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn btn-primary" onClick={saveContext} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer la mémoire'}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Nomenclature automatique</div>
        <div className="card-meta" style={{ marginBottom: 8 }}>
          Gabarit de nommage. Variables disponibles : <code>{'{date}'}</code>,{' '}
          <code>{'{discipline}'}</code>, <code>{'{phase}'}</code>, <code>{'{type}'}</code>,{' '}
          <code>{'{version}'}</code>, <code>{'{auteur}'}</code>.
        </div>
        <input
          value={nomenclaturePattern}
          onChange={(e) => setNomenclaturePattern(e.target.value)}
          placeholder="{date}_{discipline}_{phase}_{type}_v{version}"
          style={{
            width: '100%',
            padding: 10,
            border: '1px solid var(--c-border)',
            borderRadius: 6,
            background: 'var(--c-bg)',
            fontFamily: 'monospace',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={nomenclatureEnabled}
              onChange={(e) => setNomenclatureEnabled(e.target.checked)}
            />
            Activer à la création de document
          </label>
          <button className="btn btn-primary" onClick={saveNomenclature}>
            Enregistrer
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Délégations de validation</div>
        <div className="card-meta" style={{ marginBottom: 8 }}>
          Permets à un autre membre de valider à ta place (congés, absence…).
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px auto', gap: 8, alignItems: 'flex-end' }}>
          <div className="field" style={{ margin: 0 }}>
            <label>Déléguer à</label>
            <select value={delegateTo} onChange={(e) => setDelegateTo(e.target.value)}>
              <option value="">—</option>
              {detail.members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Note (facultatif)</label>
            <input value={delegateNote} onChange={(e) => setDelegateNote(e.target.value)} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Expire le</label>
            <input type="date" value={delegateExpires} onChange={(e) => setDelegateExpires(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={createDelegation} disabled={!delegateTo}>
            Déléguer
          </button>
        </div>

        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {delegations.length === 0 && <div className="card-meta">Aucune délégation.</div>}
          {delegations.map((d) => (
            <div
              key={d.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 10px',
                borderRadius: 6,
                background: d.active ? 'var(--c-nav)' : 'transparent',
                opacity: d.active ? 1 : 0.55,
              }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>
                  {d.from_name} → {d.to_name}
                </div>
                <div className="card-meta">
                  {d.active ? 'active' : 'révoquée'}
                  {d.expires_at ? ` · expire le ${d.expires_at.slice(0, 10)}` : ''}
                  {d.note ? ` · ${d.note}` : ''}
                </div>
              </div>
              {d.active === 1 && (
                <button
                  className="btn btn-ghost"
                  style={{ padding: '4px 10px', fontSize: 12 }}
                  onClick={() => revokeDelegation(d.id)}
                >
                  Révoquer
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Appliquer un gabarit</div>
        <div className="card-meta" style={{ marginBottom: 8 }}>
          Remplace modules, catégories, workflow, dossiers par ceux du gabarit sélectionné.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            style={{
              flex: 1,
              padding: 10,
              border: '1px solid var(--c-border)',
              borderRadius: 6,
            }}
          >
            <option value="">—</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.description ? ` — ${t.description}` : ''}
              </option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={applyTemplate} disabled={!selectedTemplate}>
            Appliquer
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Membres</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {detail.members.map((m) => (
            <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div>{m.name}</div>
                <div className="card-meta">{m.email}</div>
              </div>
              <div className="card-meta">{m.role}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 40, borderColor: '#f5c9bd' }}>
        <div className="card-title" style={{ color: '#8a2f1c' }}>
          Archivage
        </div>
        <div className="card-meta" style={{ marginBottom: 10 }}>
          Le projet disparaît de la liste active mais reste récupérable via « Voir archivés ».
        </div>
        <button className="btn btn-revoir" onClick={archiveProject}>
          Archiver ce projet
        </button>
      </div>
    </>
  );
}
